import React from 'react'
import { Physics } from '@react-three/rapier'
import type { Vector3Tuple } from 'three'

/**
 * Physics Configuration Interface
 *
 * Defines all configurable physics parameters for the Rapier physics world.
 * These settings are optimized for small brick objects with precise collision detection.
 */
export interface PhysicsConfig {
  /** Gravity vector [x, y, z] - typically [0, -9.81, 0] for realistic Earth gravity */
  gravity: Vector3Tuple

  /** Enable debug visualization in development mode */
  debug: boolean

  /** Physics simulation timestep in seconds (1/60 = 60 FPS) */
  timeStep: number

  /** Number of solver iterations per step (higher = more stable, slower) */
  numSolverIterations: number

  /** Number of additional friction iterations per step (higher = better contact resolution) */
  numAdditionalFrictionIterations: number

  /** Number of internal PGS iterations (improves stability with less cost than numSolverIterations) */
  numInternalPgsIterations: number

  /** Pause the physics simulation */
  paused: boolean

  /** Contact natural frequency - affects error reduction (higher = more correction) */
  contactNaturalFrequency: number

  /** Approximate size of most dynamic objects (units per meter) */
  lengthUnit: number
}

/**
 * Default Physics Configuration
 *
 * Optimized for brick building:
 * - Standard Earth gravity for realistic stacking
 * - Higher solver iterations for stable brick connections
 * - Small timestep for precise collision detection
 * - Debug mode tied to development environment
 */
const DEFAULT_PHYSICS_CONFIG: PhysicsConfig = {
  // Realistic gravity: 9.81 m/s² downward (Y-axis)
  gravity: [0, -9.81, 0],

  // Enable debug visualization in development mode
  // Vite exposes import.meta.env.DEV for development mode
  debug: import.meta.env.DEV && import.meta.env.VITE_PHYSICS_DEBUG === 'true',

  // 60 FPS physics simulation (1/60 ≈ 0.0167 seconds)
  // Smaller timestep = more accurate but more CPU intensive
  timeStep: 1 / 60,

  // Solver iterations: Balance between stability and performance
  // Higher values prevent brick jitter and improve stacking stability
  // Default: 4, Brick building optimal: 8
  numSolverIterations: 8,

  // Additional friction iterations for better contact resolution
  // Helps small bricks maintain stable connections
  // Default: 4, Brick building optimal: 4
  numAdditionalFrictionIterations: 4,

  // Internal PGS iterations - improves stability with less CPU cost
  // Default: 1, we keep it at 1 for performance
  numInternalPgsIterations: 1,

  // Physics simulation active by default
  paused: false,

  // Contact natural frequency - affects how errors are reduced
  // Higher value = more aggressive error correction
  // Default: 30, optimal for rigid bricks: 30
  contactNaturalFrequency: 30,

  // Length unit - approximate size of dynamic objects in scene
  // LEGO bricks are small (0.8 units = 8mm), so we use 0.8
  // This helps Rapier optimize tolerances for small objects
  lengthUnit: 0.8,
}

interface PhysicsSetupProps {
  children: React.ReactNode
  /** Optional custom physics configuration - merges with defaults */
  config?: Partial<PhysicsConfig>
}

/**
 * PhysicsSetup Component
 *
 * Wraps the Rapier physics world with optimized settings for brick building.
 *
 * Configuration Highlights:
 * - Gravity: -9.81 Y-axis for realistic brick stacking
 * - High solver iterations (8) for stable brick connections
 * - Small timestep (1/60s) for precise collision detection
 * - Debug mode available via VITE_PHYSICS_DEBUG=true environment variable
 * - Optimized for small objects (bricks) with exact collision shapes
 *
 * Performance Considerations:
 * - numSolverIterations affects CPU usage linearly
 * - timeStep affects both accuracy and performance
 * - For 500+ bricks, consider reducing iterations to 6 if FPS drops
 * - Physics bodies can be put to sleep when static to save CPU
 *
 * Usage:
 * ```tsx
 * <PhysicsSetup>
 *   <Ground />
 *   <Brick />
 * </PhysicsSetup>
 * ```
 *
 * @component
 */
export const PhysicsSetup: React.FC<PhysicsSetupProps> = ({
  children,
  config = {},
}) => {
  // Merge custom config with defaults
  const physicsConfig: PhysicsConfig = {
    ...DEFAULT_PHYSICS_CONFIG,
    ...config,
  }

  return (
    <Physics
      gravity={physicsConfig.gravity}
      debug={physicsConfig.debug}
      timeStep={physicsConfig.timeStep}
      paused={physicsConfig.paused}
      numSolverIterations={physicsConfig.numSolverIterations}
      numAdditionalFrictionIterations={physicsConfig.numAdditionalFrictionIterations}
      numInternalPgsIterations={physicsConfig.numInternalPgsIterations}
      contactNaturalFrequency={physicsConfig.contactNaturalFrequency}
      lengthUnit={physicsConfig.lengthUnit}
      // Disable automatic collider generation - we'll manually create precise colliders
      colliders={false}
    >
      {children}
    </Physics>
  )
}

/**
 * Export default configuration for testing and reference
 */
export { DEFAULT_PHYSICS_CONFIG }
